import { Identifier } from 'react-admin';
import { axiosInstance } from 'services/http';
import {
  JobRepairBuilderSupervisorEmailDto,
  JobRepairDataDto,
} from '@vatos-pas/common';

const getEmailContent = async (repairId: Identifier) => {
  const builderSupervisorEmail =
    await axiosInstance.get<JobRepairBuilderSupervisorEmailDto>(
      `/job-repair/${repairId}/builder-supervisor-email`,
    );

  return builderSupervisorEmail.data;
};

const getLoggedUserInfo = async (id: Identifier) => {
  const userLogged = await axiosInstance.get<any>(`/user/${id}`);

  return userLogged.data;
};

interface SupervisorData {
  firstName: string;
  lastName: string;
  email: string;
  primaryPhone: string;
}

const getBuilderSupervisorEmailBody = (
  jobRepairData: JobRepairDataDto,
  supervisors: SupervisorData[],
) => {
  // Build reply-to email list
  const replyToEmails = supervisors.map(s => s.email);
  let replyToText = '';

  if (replyToEmails.length === 1) {
    replyToText = `Please approve the following and send the PO or a payment screenshot by replying to ${replyToEmails[0]}`;
  } else if (replyToEmails.length > 1) {
    const lastEmail = replyToEmails[replyToEmails.length - 1];
    const restEmails = replyToEmails.slice(0, -1).join(', ');
    replyToText = `Please approve the following and send the PO or a payment screenshot by replying to ${restEmails}, and/or ${lastEmail}`;
  }

  // Build signature blocks for supervisors
  const supervisorLines = supervisors
    .map(s => `${s.firstName} ${s.lastName}\r\n${s.primaryPhone}\r\n${s.email}`)
    .join('\r\n\r\n');

  // API returns repair description (e.g. "H/O Service") instead of RepairTypeEnum value
  const scheduledForLine =
    jobRepairData.repairType === 'H/O Service'
      ? 'This repair is scheduled for:\r\n\r\n'
      : '';

  return `Greetings,\r\n\r\n${scheduledForLine}${replyToText}\r\n\r\n\u2003\u2003\u2003${
    jobRepairData.builderName
  }\r\n\u2003\u2003\u2003${
    jobRepairData.subDivisionName
  }\r\n\u2003\u2003\u2003Lot: ${jobRepairData.lot}\r\n\u2003\u2003\u2003${
    jobRepairData?.jobBlock ? `Block: ${jobRepairData?.jobBlock}` : ''
  }\r\n\u2003\u2003\u2003${jobRepairData.address1}\r\n\u2003\u2003\u2003${
    jobRepairData?.address2 ? `${jobRepairData?.address2}` : ''
  }\r\n\u2003\u2003\u2003${jobRepairData?.city}, ${jobRepairData?.state} ${
    jobRepairData?.zip
  }\r\n\r\n\u2003\u2003\u2003$ ${jobRepairData.poAmount}\r\n\u2003\u2003\u2003${
    jobRepairData?.jobRepairDescription || ''
  }\r\n\u2003\u2003\u2003\r\nVatos Supervisor\r\n\r\n${supervisorLines}\r\n\r\nVatos Drywall\r\nPhone: 407-365-3303
`;
};

const openEmailClient = (to: string, body: string, subject: string) => {
  window.location.href = `mailto:${to}?body=${encodeURI(
    body,
  )}&subject=${subject}`;
};

const sendMail = async (repairId: Identifier | undefined) => {
  if (!repairId) return;

  const builderSupervisorEmail: any = await getEmailContent(repairId);

  const userId: string = localStorage.getItem('userId') || '';
  const userInfo = await getLoggedUserInfo(userId);

  if (!userInfo) return;

  // Check if logged user is a Supervisor
  const isSupervisor = userInfo.userRoles?.some(
    (ur: any) => ur.role?.name === 'Supervisor',
  );

  let supervisorsForEmail: SupervisorData[] = [];

  if (isSupervisor) {
    // If creator is Supervisor, use only their contact details
    supervisorsForEmail = [
      {
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        email: userInfo.email,
        primaryPhone: userInfo.primaryPhone,
      },
    ];
  } else {
    // If creator is not Supervisor, use Job's assigned Supervisor(s)
    const jobSupervisors =
      builderSupervisorEmail.jobRepairData.subdivisionSupervisors || [];

    // Filter only active supervisors and sort alphabetically by firstName
    supervisorsForEmail = jobSupervisors
      .filter((s: any) => s.active)
      .sort((a: any, b: any) => a.firstName.localeCompare(b.firstName));
  }

  const builderSupervisorEmailBody = getBuilderSupervisorEmailBody(
    builderSupervisorEmail.jobRepairData,
    supervisorsForEmail,
  );

  const emailDestination = builderSupervisorEmail.to?.join(';');

  const { subDivisionName, lot, repairType, poAmount } =
    builderSupervisorEmail.jobRepairData;

  const subject =
    [subDivisionName, lot, repairType || 'Repair'].filter(Boolean).join(' ') +
    ` - $${Number(poAmount).toFixed(2)}`;

  openEmailClient(emailDestination, builderSupervisorEmailBody, subject);
};

export { sendMail };
